import React, { useState, useEffect, useImperativeHandle, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import QuizSlide from './QuizSlide';
import {
  useGetQuestionsQuery,
  useSaveIncompleteQuizMutation,
  useGetIncompleteQuizQuery,
  useSaveQuizProgressMutation,
} from '../../features/quizApiSlice';
import useMeta from '../../hooks/useMeta';
import {
  EMAIL_SLIDE_CODE,
  getEmailForIncompleteSave,
  getNextActiveSlide,
  getPreviousActiveSlide,
  initializeSlideNavigation,
  normalizeSavedPositionCode,
  normalizeSavedSlideNavigation,
  normalizeSavedSlideValues,
  buildPrefillSlideValues,
  buildDefaultCountrySlideValues,
  normalizeQuizDefinition,
  sortQuizSlidesByPosition,
} from './utils/quizHelpers';
import { getInsightForSlide } from './config/conditionalInsights';
import {
  FEMALE_GENDER_VALUE,
  GENDER_SLIDE_CODE,
  MENOPAUSE_FOCUS_AREA_NAME,
  MENOPAUSE_FOLLOWUP_SLIDE_CODES,
} from './config/quizConfig';
import { validateSlide } from './utils/quizValidation';
import {
  FOCUS_AREAS_SLIDE_CODE,
  FOCUS_PRIORITY_FIELD,
  FOCUS_PRIORITY_SLIDE_CODE,
  buildAutoFocusPriority,
  getFocusAreaOptionsFromQuizData,
  getSelectedFocusAreas,
  normalizeFocusPriority,
} from './utils/focusPriority';
import SkeletonLoader from '../elements/SkeletonLoader';
import { normalizeLanguageCode } from '../../utils/language';

const QUIZ_PROGRESS_SESSION_ID_KEY = 'quizProgressSessionId';
const QUIZ_PROGRESS_SAVE_DELAY_MS = 500;

const clearMenopauseAnswers = (values = {}, focusAreaOptions = []) => {
  const nextValues = { ...values };
  const focusAreas = { ...(nextValues[FOCUS_AREAS_SLIDE_CODE] || {}) };
  delete focusAreas[MENOPAUSE_FOCUS_AREA_NAME];

  if (Object.keys(focusAreas).length > 0) {
    nextValues[FOCUS_AREAS_SLIDE_CODE] = focusAreas;
  } else {
    delete nextValues[FOCUS_AREAS_SLIDE_CODE];
  }

  MENOPAUSE_FOLLOWUP_SLIDE_CODES.forEach((code) => {
    delete nextValues[code];
  });

  const availableFocusAreas = focusAreaOptions.filter((option) => option.name !== MENOPAUSE_FOCUS_AREA_NAME);
  const selectedFocusAreas = getSelectedFocusAreas(nextValues, availableFocusAreas);
  const focusPriority = nextValues[FOCUS_PRIORITY_SLIDE_CODE]?.[FOCUS_PRIORITY_FIELD]?.value || [];

  if (selectedFocusAreas.length === 1) {
    nextValues[FOCUS_PRIORITY_SLIDE_CODE] = {
      [FOCUS_PRIORITY_FIELD]: {
        value: buildAutoFocusPriority(selectedFocusAreas),
        answerCode: null,
      },
    };
    return nextValues;
  }

  if (selectedFocusAreas.length > 1) {
    nextValues[FOCUS_PRIORITY_SLIDE_CODE] = {
      [FOCUS_PRIORITY_FIELD]: {
        value: normalizeFocusPriority(focusPriority, selectedFocusAreas),
        answerCode: null,
      },
    };
    return nextValues;
  }

  delete nextValues[FOCUS_PRIORITY_SLIDE_CODE];
  return nextValues;
};

const getStoredQuizProgressSessionId = () => {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(QUIZ_PROGRESS_SESSION_ID_KEY) || '';
};

const getOrCreateQuizProgressSessionId = () => {
  const existingSessionId = getStoredQuizProgressSessionId();
  if (existingSessionId) return existingSessionId;

  const sessionId = uuidv4();
  sessionStorage.setItem(QUIZ_PROGRESS_SESSION_ID_KEY, sessionId);
  return sessionId;
};

const clearQuizProgressSessionId = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(QUIZ_PROGRESS_SESSION_ID_KEY);
};

const QuizModule = React.forwardRef(
  (
    {
      setProgressValue,
      setShowProgressBar,
      progressValue,
      onStateChange,
      isClassmedMode = false,
      classmedId = '',
      classmedPharmacyId = '',
      forcedLang = '',
      initialIncompleteQuiz = null,
      initialQuestions = null,
    },
    ref,
  ) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { metaEvent } = useMeta();
    const [searchParams] = useSearchParams();

    const quizId = searchParams.get('quizId');
    const partnerId = searchParams.get('partner_id');

    const [lang, setLang] = useState(forcedLang);
    const [quizData, setQuizData] = useState([]);
    const [currentPositionCode, setCurrentPositionCode] = useState(1);
    const [slideValues, setSlideValues] = useState({});
    const [slideTransitionClass, setSlideTransitionClass] = useState('');
    const [slideNavigation, setSlideNavigation] = useState({});
    const [canPrefetchResults, setCanPrefetchResults] = useState(false);
    const [isQuizProgressStarted, setIsQuizProgressStarted] = useState(false);

    const quizSlideRef = useRef();
    const pendingIncompleteSaveRef = useRef(null);
    const quizProgressSaveTimeoutRef = useRef(null);
    const lastQuizProgressPayloadRef = useRef('');

    const normalizedQuiz = useMemo(() => normalizeQuizDefinition(quizData), [quizData]);
    const { slidesByPosition, slideCodeMap } = normalizedQuiz;

    const [saveIncompleteQuiz] = useSaveIncompleteQuizMutation();
    const [saveQuizProgress] = useSaveQuizProgressMutation();
    const hasInitialQuestions = Array.isArray(initialQuestions) && initialQuestions.length > 0;
    const { data: fetchedQuestions, isSuccess } = useGetQuestionsQuery(lang, {
      skip: !lang || hasInitialQuestions,
    });
    const questionsResponse = useMemo(
      () => (hasInitialQuestions ? { data: initialQuestions } : fetchedQuestions),
      [fetchedQuestions, hasInitialQuestions, initialQuestions],
    );
    const isQuestionsReady = hasInitialQuestions || isSuccess;
    const { data: incompleteQuizResponse } = useGetIncompleteQuizQuery(quizId, {
      skip: !quizId || !!initialIncompleteQuiz,
    });
    const incompleteQuiz = initialIncompleteQuiz || incompleteQuizResponse?.data || null;

    useEffect(() => {
      if (forcedLang) {
        setLang(normalizeLanguageCode(forcedLang));
        setIsQuizProgressStarted(Boolean(getStoredQuizProgressSessionId()));
        return;
      }

      const storedLanguage = localStorage.getItem('lang');
      setLang(normalizeLanguageCode(storedLanguage));
      setIsQuizProgressStarted(Boolean(getStoredQuizProgressSessionId()));
    }, [forcedLang]);

    const buildQuizProgressPayload = useCallback(
      (overrides = {}) => {
        const sessionId = getStoredQuizProgressSessionId();
        if (!sessionId) return null;

        return {
          sessionId,
          data: slideValues,
          currentSlide: overrides.currentSlide ?? currentPositionCode,
          progressBar: overrides.progressBar ?? progressValue,
          slideNavigation,
        };
      },
      [currentPositionCode, progressValue, slideNavigation, slideValues],
    );

    const saveQuizProgressSnapshot = useCallback(
      async (overrides = {}) => {
        const payload = buildQuizProgressPayload(overrides);
        if (!payload) return null;

        return saveQuizProgress(payload).unwrap();
      },
      [buildQuizProgressPayload, saveQuizProgress],
    );

    // Load incomplete quiz data when we have both the payload and questions (so we can normalize legacy format)
    useEffect(() => {
      if (!quizId || !incompleteQuiz || !questionsResponse?.data) return;

      const incomplete = incompleteQuiz;
      const questions = sortQuizSlidesByPosition(questionsResponse.data);
      const isLegacyNav = Array.isArray(incomplete.slideNavigation);

      const normalizedData = normalizeSavedSlideValues(incomplete.data, questions);
      const normalizedPosition = normalizeSavedPositionCode(incomplete.currentSlide, questions, isLegacyNav);
      const normalizedNav = normalizeSavedSlideNavigation(
        incomplete.slideNavigation,
        questions,
        normalizedPosition,
      );

      setCurrentPositionCode(normalizedPosition);
      updateProgressBar(normalizedPosition - 1, questions.length);
      setSlideValues(normalizedData);
      setSlideNavigation(normalizedNav);

      if (incomplete.progressBar === 100 && incomplete.quiz_id > 0) {
        localStorage.setItem('quiz_id', incomplete.quiz_id);
        localStorage.setItem('email', incomplete.email);
        navigate(`/product-suggestion${location.search}`, {
          replace: true,
          state: { returningUser: true, quiz_id: incomplete.quiz_id },
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizId, incompleteQuiz, questionsResponse, isQuestionsReady]);

    // Save incomplete quiz after state has committed (so we send updated slideValues)
    useEffect(() => {
      const pending = pendingIncompleteSaveRef.current;
      if (!pending || pending.nextActivePositionCode !== currentPositionCode) return;
      pendingIncompleteSaveRef.current = null;
      if (isClassmedMode) return;

      const email = getEmailForIncompleteSave(slideValues);
      if (!email) return;
      saveIncompleteQuiz({
        email,
        currentSlide: currentPositionCode,
        slideNavigation,
        progressBar: pending.isLastQuestion ? 100 : progressValue,
        data: slideValues,
        partner_id: partnerId,
        isCustomQuiz: location.pathname.includes('/custom-quiz'),
        lang,
      })
        .then((res) => {
          localStorage.setItem('email', res?.data?.data?.email);
          if (pending.currentSlideCode === EMAIL_SLIDE_CODE) {
            metaEvent('CompleteRegistration');
            setCanPrefetchResults(true);
          }
        })
        .catch((error) => {
          console.error('Error saving incomplete quiz:', error);
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPositionCode, isClassmedMode, slideValues, slideNavigation, progressValue]);

    useEffect(() => {
      if (!isQuizProgressStarted || quizId || currentPositionCode <= 1) return undefined;
      if (Object.keys(slideNavigation).length === 0 || Object.keys(slideValues).length === 0)
        return undefined;

      const payload = buildQuizProgressPayload();
      if (!payload) return undefined;

      const payloadKey = JSON.stringify(payload);
      if (payloadKey === lastQuizProgressPayloadRef.current) return undefined;

      if (quizProgressSaveTimeoutRef.current) {
        clearTimeout(quizProgressSaveTimeoutRef.current);
      }

      quizProgressSaveTimeoutRef.current = setTimeout(() => {
        saveQuizProgress(payload)
          .unwrap()
          .then(() => {
            lastQuizProgressPayloadRef.current = payloadKey;
          })
          .catch((error) => {
            console.error('Error saving quiz progress:', error);
          });
      }, QUIZ_PROGRESS_SAVE_DELAY_MS);

      return () => {
        if (quizProgressSaveTimeoutRef.current) {
          clearTimeout(quizProgressSaveTimeoutRef.current);
        }
      };
    }, [
      buildQuizProgressPayload,
      currentPositionCode,
      isQuizProgressStarted,
      quizId,
      saveQuizProgress,
      slideNavigation,
      slideValues,
    ]);

    const handleQuizComplete = useCallback(async () => {
      if (quizProgressSaveTimeoutRef.current) {
        clearTimeout(quizProgressSaveTimeoutRef.current);
      }

      if (isQuizProgressStarted && !quizId && currentPositionCode > 1) {
        try {
          await saveQuizProgressSnapshot({ progressBar: 100 });
        } catch (error) {
          console.error('Error saving final quiz progress:', error);
        }
      }

      clearQuizProgressSessionId();
      setIsQuizProgressStarted(false);
      lastQuizProgressPayloadRef.current = '';
    }, [currentPositionCode, isQuizProgressStarted, quizId, saveQuizProgressSnapshot]);

    useEffect(() => {
      if (isQuestionsReady && questionsResponse?.data) {
        const questions = sortQuizSlidesByPosition(questionsResponse.data);
        setQuizData(questions);

        if (!quizId) {
          // Initialize slide navigation from isMendatory (mandatory = active, optional = inactive)
          if (Object.keys(slideNavigation).length === 0) {
            const initialNavigation = initializeSlideNavigation(questions);
            if (isClassmedMode) {
              const initialQuizDefinition = normalizeQuizDefinition(questions);
              const emailPositionCode =
                initialQuizDefinition.slideCodeMap.codeToPosition.get(EMAIL_SLIDE_CODE);
              if (emailPositionCode && initialNavigation[emailPositionCode]) {
                initialNavigation[emailPositionCode] = {
                  ...initialNavigation[emailPositionCode],
                  active: false,
                };
              }
            }
            setSlideNavigation(initialNavigation);
            setCurrentPositionCode(1);
            const countryDefaults = buildDefaultCountrySlideValues(questions);
            const prefill = buildPrefillSlideValues(questions, {
              name: location.state?.prefilledName,
              email: location.state?.prefilledEmail,
            });
            setSlideValues((prev) => {
              const next = { ...prev };
              const mergeSlice = (slice) => {
                for (const code of Object.keys(slice)) {
                  const key = Number(code);
                  next[key] = { ...(next[key] || {}), ...slice[code] };
                }
              };
              mergeSlice(countryDefaults);
              mergeSlice(prefill);
              return next;
            });
          }

          updateProgressBar(0);
          localStorage.removeItem('quiz_id');
        }

        setSlideTransitionClass('active');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionsResponse, isClassmedMode, isQuestionsReady]);

    const currentSlideCode = slideCodeMap.positionToCode.get(currentPositionCode);
    const lastPositionCodeValue =
      Object.keys(slideNavigation).length > 0 ? Math.max(...Object.keys(slideNavigation).map(Number)) : 0;
    const isLastSlide = currentPositionCode === lastPositionCodeValue;
    const currentSlide = slidesByPosition.get(currentPositionCode) || null;
    const currentRawSlide = currentSlide?.rawSlide || null;
    const nextActions = [];
    if (Array.isArray(currentRawSlide)) {
      currentRawSlide.forEach((c) => {
        if (!c) return;
        if (c.action === 'next') nextActions.push(c);
        if (c.type === 'group' && Array.isArray(c.controls)) {
          c.controls.forEach((ctrl) => {
            if (ctrl?.action === 'next') nextActions.push(ctrl);
          });
        }
      });
    }
    const choiceNexts = nextActions.filter((c) => c?.name && c.value != null && String(c.value) !== '');
    const hasChoiceNext = choiceNexts.length > 0;
    const hasChoiceNextSelection =
      !!currentSlideCode &&
      choiceNexts.some((c) => {
        const saved = slideValues?.[currentSlideCode]?.[c.name]?.value;
        return saved != null && String(saved) === String(c.value);
      });
    const hasInsight = !!getInsightForSlide(currentSlideCode, slideValues);
    const genericNextExists = nextActions.some(
      (c) => c?.action === 'next' && (!c.name || c.value == null || String(c.value) === ''),
    );
    // only validate slides where the footer is the primary "Next" (generic next exists).
    const canContinue = genericNextExists
      ? validateSlide(currentSlideCode, slideValues, quizData).valid : true;

    // Exposed to parent component (Quiz/index.jsx) for back button, continue, and state reads
    useImperativeHandle(ref, () => ({
      handlePrevious() {
        const previousActivePositionCode = getPreviousActiveSlide(currentPositionCode, slideNavigation);

        if (previousActivePositionCode === undefined) {
          navigate(-1);
          return;
        }

        setSlideTransitionClass('');
        setCurrentPositionCode(previousActivePositionCode);
        setSlideTransitionClass('active');
        updateProgressBar(previousActivePositionCode - 1);
      },
      handleContinue() {
        if (quizSlideRef.current?.handleContinue) {
          quizSlideRef.current.handleContinue();
        }
      },
    }));

    useEffect(() => {
      onStateChange?.({
        currentSlideCode,
        quizData,
        slideNavigation,
        slideValues,
        isLastSlide,
        lang,
        currentPositionCode,
        hasChoiceNext,
        hasChoiceNextSelection,
        hasInsight,
        canContinue,
      });
    }, [
      currentPositionCode,
      currentSlideCode,
      hasChoiceNext,
      hasInsight,
      hasChoiceNextSelection,
      canContinue,
      isLastSlide,
      lang,
      onStateChange,
      quizData,
      slideNavigation,
      slideValues,
    ]);

    // Moves to the next active slide
    const handleNextSlide = (newlyActivatedCodes = [], newlyDeactivatedCodes = []) => {
      setSlideTransitionClass('');

      let nextActivePositionCode;
      if (newlyActivatedCodes.length > 0 || newlyDeactivatedCodes.length > 0) {
        const tempNavigation = { ...slideNavigation };
        // Apply deactivated first so switching options (e.g. Vegan -> None) clears previous state
        newlyDeactivatedCodes.forEach((code) => {
          const positionCode = slideCodeMap.codeToPosition.get(code);
          if (positionCode !== undefined && tempNavigation[positionCode]) {
            tempNavigation[positionCode] = { ...tempNavigation[positionCode], active: false };
          }
        });
        newlyActivatedCodes.forEach((code) => {
          const positionCode = slideCodeMap.codeToPosition.get(code);
          if (positionCode !== undefined && tempNavigation[positionCode]) {
            tempNavigation[positionCode] = { ...tempNavigation[positionCode], active: true };
          }
        });
        nextActivePositionCode = getNextActiveSlide(currentPositionCode, tempNavigation);
      } else {
        nextActivePositionCode = getNextActiveSlide(currentPositionCode, slideNavigation);
      }

      const slideCodeAtNav = slideCodeMap.positionToCode.get(currentPositionCode);

      if (nextActivePositionCode !== undefined) {
        setCurrentPositionCode(nextActivePositionCode);
        setSlideTransitionClass('active');
        updateProgressBar(nextActivePositionCode - 1);
        if (slideCodeAtNav === 1) {
          getOrCreateQuizProgressSessionId();
          setIsQuizProgressStarted(true);
        }
        if (!isClassmedMode && slideCodeAtNav >= 12) {
          const lastPositionCode = Math.max(...Object.keys(slideNavigation).map(Number));
          const isLastQuestion = nextActivePositionCode === lastPositionCode;
          if (slideCodeAtNav === EMAIL_SLIDE_CODE) {
            setCanPrefetchResults(false);
          }
          pendingIncompleteSaveRef.current = {
            nextActivePositionCode,
            isLastQuestion,
            currentSlideCode: slideCodeAtNav,
          };
        }
      }
    };

    //Updates slide navigation based on slide codes
    const updateSlideNavigationByCodes = useCallback(
      (slideCodes, active) => {
        // Filter out invalid slide codes (0 or negative)
        const validSlideCodes = slideCodes.filter((code) => code > 0);

        if (validSlideCodes.length === 0) {
          return;
        }

        setSlideNavigation((prevNavigation) => {
          const updatedNavigation = { ...prevNavigation };

          validSlideCodes.forEach((code) => {
            // Convert slide code to position code
            const positionCode = slideCodeMap.codeToPosition.get(code);

            if (positionCode !== undefined && updatedNavigation[positionCode]) {
              updatedNavigation[positionCode] = { ...updatedNavigation[positionCode], active };
            }
          });

          return updatedNavigation;
        });
      },
      [slideCodeMap],
    );

    // Updates slide values based on slide code
    // slideCode - The slide code
    // key - The field key
    // value - The field value
    // answerCode - The answer code for API submission
    const updateSlideData = (slideCode, key, value, answerCode) => {
      if (key === undefined) return;
      const isGenderIneligible =
        slideCode === GENDER_SLIDE_CODE && key === 'gender' && value !== FEMALE_GENDER_VALUE;
      const isMenopauseFocusUnselected =
        slideCode === FOCUS_AREAS_SLIDE_CODE && key === MENOPAUSE_FOCUS_AREA_NAME && value === false;
      const shouldClearMenopause = isGenderIneligible || isMenopauseFocusUnselected;

      setSlideValues((prevValues) => {
        const updatedValues = { ...prevValues };
        const prevSlideEntry = updatedValues[slideCode];

        if (value === false) {
          // Remove the field if value is false (unchecked).
          // IMPORTANT: clone the nested object first because legacy-loaded payloads can be frozen.
          if (prevSlideEntry && Object.prototype.hasOwnProperty.call(prevSlideEntry, key)) {
            const nextSlideEntry = { ...prevSlideEntry };
            delete nextSlideEntry[key];

            if (Object.keys(nextSlideEntry).length === 0) {
              delete updatedValues[slideCode];
            } else {
              updatedValues[slideCode] = nextSlideEntry;
            }
          }
        } else {
          // Add or update the field
          updatedValues[slideCode] = {
            ...(prevSlideEntry || {}),
            [key]: { value, answerCode },
          };
        }

        if (shouldClearMenopause) {
          const focusAreaOptions = getFocusAreaOptionsFromQuizData(quizData).filter(
            (option) => option.name !== MENOPAUSE_FOCUS_AREA_NAME,
          );
          const clearedValues = clearMenopauseAnswers(updatedValues, focusAreaOptions);
          const selectedFocusAreas = getSelectedFocusAreas(clearedValues, focusAreaOptions);

          setSlideNavigation((prevNavigation) => {
            const updatedNavigation = { ...prevNavigation };

            MENOPAUSE_FOLLOWUP_SLIDE_CODES.forEach((code) => {
              const positionCode = slideCodeMap.codeToPosition.get(code);
              if (positionCode !== undefined && updatedNavigation[positionCode]) {
                updatedNavigation[positionCode] = { ...updatedNavigation[positionCode], active: false };
              }
            });

            const focusPriorityPosition = slideCodeMap.codeToPosition.get(FOCUS_PRIORITY_SLIDE_CODE);
            if (focusPriorityPosition !== undefined && updatedNavigation[focusPriorityPosition]) {
              updatedNavigation[focusPriorityPosition] = {
                ...updatedNavigation[focusPriorityPosition],
                active: selectedFocusAreas.length > 1,
              };
            }

            return updatedNavigation;
          });

          return clearedValues;
        }

        return updatedValues;
      });
    };

    const updateProgressBar = (orderIndex, totalSlidesOverride) => {
      const clampedIndex = Math.max(0, Number(orderIndex) || 0);
      const totalSlides =
        typeof totalSlidesOverride === 'number' && totalSlidesOverride > 0
          ? totalSlidesOverride
          : quizData.length;
      const lastSlideIndex = Math.max(0, totalSlides - 1);

      // quizData may not be populated yet when this runs (setState is async).
      // Until we know the real last slide index, keep progress at 0%.
      if (lastSlideIndex === 0 && totalSlides === 0) {
        setShowProgressBar(true);
        setProgressValue(0);
        return;
      }

      const progress =
        lastSlideIndex === 0
          ? 100
          : clampedIndex >= lastSlideIndex
            ? 100
            : (clampedIndex / lastSlideIndex) * 100;

      setShowProgressBar(true);
      setProgressValue(Math.round(Math.min(100, Math.max(0, progress))));
    };

    if (!isQuestionsReady || !questionsResponse?.data || !currentRawSlide) {
      return <SkeletonLoader />;
    }

    return (
      <div className="t-w-full t-max-w-[520px] t-mx-auto t-px-[18px] sm:t-px-6 t-pt-6 sm:t-pt-9 t-pb-[180px] sm:t-pb-[160px]">
        <div className={slideTransitionClass === 'active' ? 'quiz-q-anim' : ''}>
          <QuizSlide
            slide={currentRawSlide}
            quizData={quizData}
            handleNext={handleNextSlide}
            slideValues={slideValues}
            updateSlideData={updateSlideData}
            updateSlideNavigation={updateSlideNavigationByCodes}
            slideNavigation={slideNavigation}
            ref={quizSlideRef}
            isLastSlide={isLastSlide}
            lang={lang}
            canPrefetchResults={canPrefetchResults}
            onQuizComplete={handleQuizComplete}
            isClassmedMode={isClassmedMode}
            classmedId={classmedId}
            classmedPharmacyId={classmedPharmacyId}
          />
        </div>
      </div>
    );
  },
);

export default QuizModule;
